// src/redux/auth/authSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { markAuthReady } from "@/lib/authBoot";

import {
  registerUserApi,
  verifyUser,
  choosePathApi,
  loginUser as loginUserApi,
  logoutUser as logoutUserApi,
  forgetPassword as forgetPasswordApi,
  resetPassword as resetPasswordApi,
  changePassword as changePasswordApi,
  enablePinApi,
  disablePinApi,
  enable2FA as enable2FAApi,
  verify2FAToken as verify2FATokenApi,
  disable2FA as disable2FAApi,
  verifyDisable2FAToken as verifyDisable2FATokenApi,
  loginWith2FA as loginWith2FAApi,
  sendDeleteConfirmation as sendDeleteConfirmationApi,
  confirmDeleteAccount as confirmDeleteAccountApi,
  sendReactivateConfirmation as sendReactivateConfirmationApi,
  confirmReactivateAccount as confirmReactivateAccountApi,
  fetchAuthMeApi,
  fetchProfileLanguagesApi,
  completeProfileLanguageApi,
} from "../../services/profiles/authApi";
import { logoutAllLITShieldScopes } from "../litshield/litshieldSlice";
import websocketManager from "../../lib/websocketManager";
import { setPrivateIntegrationGateState } from "../../utils/security/privateIntegrationGate";
import { clearSearchResults } from "../../redux/network/friendshipSlice";
import { clearPresence } from "../../redux/presence/presenceSlice";
import { ProfileLanguagesResponseType } from "../../types/profiles/authTypes";
import { logger } from "../../utils/logging/logger";

interface AuthState {
  token: string | null;
  user: any | null;
  loading: boolean;
  languageLoading: boolean;
  languageOptions: ProfileLanguagesResponseType | null;
  error: string | Record<string, any> | null;
  successMessage: string | null;
  isLoggedIn: boolean;
  hasCompletedSignup: boolean;
  resetPasswordSuccess: boolean;
}

const initialState: AuthState = {
  token: null,
  user: null,
  loading: false,
  languageLoading: false,
  languageOptions: null,
  error: null,
  successMessage: null,
  isLoggedIn: false,
  hasCompletedSignup: false,
  resetPasswordSuccess: false,
};

// Thunk for fetching auth user -------------------------------------------------------------------
export const fetchAuthMe = createAsyncThunk(
  "auth/fetchMe",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchAuthMeApi();
      return data; // ← فقط user
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch auth user",
      );
    }
  },
);

// Thunk for registering user ---------------------------------------------------------------------
export const register = createAsyncThunk(
  "auth/register",
  async (
    {
      email,
      password,
      agreeToTerms,
      invite_code,
    }: {
      email: string;
      password: string;
      agreeToTerms: boolean;
      invite_code?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await registerUserApi(
        email,
        password,
        agreeToTerms,
        invite_code,
      );
      return data;
    } catch (err: any) {
      const errorMessage =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Registration failed. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for verifying user
export const verify = createAsyncThunk(
  "auth/verify",
  async (activeCode: string, { rejectWithValue }) => {
    try {
      const data = await verifyUser(activeCode);
      return data;
    } catch (err: any) {
      const errorMessage =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Verification failed. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for answering questions
export const choosePath = createAsyncThunk(
  "auth/choosePath",
  async (category: string, { rejectWithValue }) => {
    try {
      const data = await choosePathApi(category);
      return data;
    } catch (err: any) {
      const errorMessage =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Something went wrong. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch profile languages -------------------------------------------------------
export const fetchProfileLanguages = createAsyncThunk(
  "auth/fetchProfileLanguages",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchProfileLanguagesApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.response?.data?.detail ||
          "Unable to load languages.",
      );
    }
  },
);

// Complete profile language onboarding -----------------------------------------
export const completeProfileLanguage = createAsyncThunk(
  "auth/completeProfileLanguage",
  async (primaryLanguage: string, { rejectWithValue }) => {
    try {
      return await completeProfileLanguageApi(primaryLanguage);
    } catch (err: any) {
      const data = err.response?.data;

      const primaryError = Array.isArray(data?.primary_language)
        ? data.primary_language[0]
        : data?.primary_language;

      return rejectWithValue(
        primaryError ||
          data?.error ||
          data?.message ||
          data?.detail ||
          "Unable to save your language.",
      );
    }
  },
);

// Thunk for logging in -------------------------------------------------------------
export const login = createAsyncThunk(
  "auth/login",
  async (
    {
      identifier,
      password,
    }: {
      identifier: string;
      password: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await loginUserApi(identifier, password);

      // Deleted account can only go through reactivation flow.
      if (data.is_deleted) {
        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("user", JSON.stringify(data.user || {}));

        setPrivateIntegrationGateState("logged_out");
        websocketManager.hardStopForLogout();

        return {
          ...data,
          is_deleted: true,
          message: data.message,
          deletion_requested_at: data.deletion_requested_at,
        };
      }

      // If 2FA is required, do not start app/private integrations yet.
      if (data.two_factor_enabled) {
        localStorage.setItem("user_email", data.email || identifier);
        setPrivateIntegrationGateState("logged_out");
        websocketManager.hardStopForLogout();

        return {
          ...data,
          two_factor_enabled: true,
        };
      }

      // Normal login completed.
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      localStorage.setItem("is_member", data.is_member ? "true" : "false");
      localStorage.setItem("user", JSON.stringify(data.user || {}));

      document.cookie = `access_token=${data.access}; Path=/; Max-Age=300; Secure; SameSite=Lax`;

      const languageCompleted =
        data.user?.language_onboarding_completed !== false;

      setPrivateIntegrationGateState(
        languageCompleted
          ? data.is_member
            ? "pending"
            : "allowed"
          : "logged_out",
        data.user?.id ?? data.user_id ?? null,
      );

      websocketManager.resetAfterLogin();

      return {
        ...data,
        two_factor_enabled: data.two_factor_enabled,
      };
    } catch (err: any) {
      const resData = err.response?.data;

      if (resData?.error && typeof resData.error === "object") {
        return rejectWithValue({
          fieldErrors: resData.error,
        });
      }

      return rejectWithValue({
        message:
          resData?.message ||
          resData?.error ||
          "Login failed. Please check your email/username and password.",
      });
    }
  },
);

// Thunk for logging in with 2FA ------------------------------------------------
export const loginWith2FA = createAsyncThunk(
  "auth/loginWith2FA",
  async (
    {
      otpCode,
      identifier,
    }: {
      otpCode: string;
      identifier: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await loginWith2FAApi(otpCode, identifier);

      // Pending deletion account receives a limited authenticated session.
      if (data.is_deleted) {
        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        localStorage.setItem("user_id", String(data.user_id));
        localStorage.setItem("user", JSON.stringify(data.user || {}));

        localStorage.removeItem("is_member");
        localStorage.removeItem("language_onboarding_pending");

        setPrivateIntegrationGateState("logged_out");

        websocketManager.hardStopForLogout();

        return {
          ...data,
          is_deleted: true,
        };
      }

      // Normal 2FA login completed.
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      localStorage.setItem("is_member", data.is_member ? "true" : "false");
      localStorage.setItem("user", JSON.stringify(data.user));

      document.cookie = `access_token=${data.access}; Path=/; Max-Age=300; Secure; SameSite=Lax`;

      const languageCompleted =
        data.user?.language_onboarding_completed !== false;

      setPrivateIntegrationGateState(
        languageCompleted
          ? data.is_member
            ? "pending"
            : "allowed"
          : "logged_out",
        data.user?.id ?? data.user_id ?? null,
      );
      websocketManager.resetAfterLogin();

      return data;
    } catch (err: any) {
      const responseData = err.response?.data;

      if (responseData?.error && typeof responseData.error === "object") {
        return rejectWithValue({
          fieldErrors: responseData.error,
        });
      }

      return rejectWithValue({
        message:
          responseData?.message || responseData?.error || "An error occurred.",
      });
    }
  },
);

// Thunk for logging out -----------------------------------------------------------
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch, rejectWithValue }) => {
    let serverLogoutError: any = null;

    try {
      const gateState = localStorage.getItem(
        "townlit_private_integrations_gate",
      );
      const isRestrictedOwnerLogout = gateState === "restricted";

      const refreshToken = localStorage.getItem("refresh_token");

      const deviceId = (localStorage.getItem("device_id") || "")
        .trim()
        .toLowerCase();

      // Stop realtime before any async cleanup can trigger reconnect.
      websocketManager.hardStopForLogout();

      // Mark private integrations as logged out immediately.
      localStorage.setItem("townlit_private_integrations_gate", "logged_out");

      // Best-effort server logout.
      if (refreshToken) {
        try {
          await logoutUserApi(refreshToken, deviceId);
        } catch (err: any) {
          serverLogoutError = err;
        }
      }

      // Best-effort LITShield cleanup.
      // Restricted owner mode is already blocked by account gate,
      // so do not call /security/logout/ in that state.
      if (!isRestrictedOwnerLogout) {
        try {
          await dispatch(logoutAllLITShieldScopes()).unwrap();
        } catch {
          // Do not block logout.
        }
      }

      // Remove sensitive storage.
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user_email");
      localStorage.removeItem("is_member");
      localStorage.removeItem("user_id");
      localStorage.removeItem("user");

      localStorage.removeItem("persist:auth");
      localStorage.removeItem("persist:profile");
      localStorage.removeItem("persist:conversation");

      localStorage.removeItem("townlit_private_integrations_gate");
      localStorage.removeItem("language_onboarding_pending");

      // Keep device_id for device continuity.
      // localStorage.removeItem("device_id");

      document.cookie = "access_token=; Path=/; Max-Age=0; SameSite=Lax";

      dispatch(clearPresence());
      dispatch(clearSearchResults());
      dispatch(setLogout());

      dispatch({ type: "auth/resetSession" });

      return {
        ok: true,
        serverLogoutFailed: Boolean(serverLogoutError),
      };
    } catch (err: any) {
      try {
        websocketManager.hardStopForLogout();

        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        localStorage.removeItem("is_member");
        localStorage.removeItem("user_id");
        localStorage.removeItem("persist:auth");
        localStorage.removeItem("persist:profile");
        localStorage.removeItem("persist:conversation");
        localStorage.removeItem("townlit_private_integrations_gate");
        localStorage.removeItem("language_onboarding_pending");

        document.cookie = "access_token=; Path=/; Max-Age=0; SameSite=Lax";

        dispatch(clearPresence());
        dispatch(clearSearchResults());
        dispatch(setLogout());

        dispatch({ type: "auth/resetSession" });

        return {
          ok: true,
          fallback: true,
        };
      } catch {
        return rejectWithValue(err.response?.data || "Logout failed");
      }
    }
  },
);

// Thunk for forgetting password --------------------------------------------------------
export const forgetPassword = createAsyncThunk(
  "auth/forgetPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      const data = await forgetPasswordApi(email);
      return data;
    } catch (err: any) {
      const errorMessage =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Failed to send reset link. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for resetting password
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { resetToken, newPassword }: { resetToken: string; newPassword: string },
    { rejectWithValue },
  ) => {
    try {
      const data = await resetPasswordApi(resetToken, newPassword);
      return data;
    } catch (err: any) {
      const errorMessage =
        typeof err.response?.data === "string"
          ? err.response.data
          : err.response?.data?.message ||
            err.response?.data?.detail ||
            err.message ||
            "Failed to reset password. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for changing password ---------------------------------------------------------
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    { oldPassword, newPassword }: { oldPassword: string; newPassword: string },
    { rejectWithValue },
  ) => {
    try {
      const data = await changePasswordApi(oldPassword, newPassword);
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to change password. Please try again.";

      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for enabling PIN security ----------------------------------------------------
export const enablePin = createAsyncThunk(
  "auth/enablePin",
  async (
    { accessPin, deletePin }: { accessPin: string; deletePin: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const data = await enablePinApi(accessPin, deletePin);
      dispatch(updatePinStatus(true));
      await dispatch(fetchAuthMe());

      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for disabling PIN security
export const disablePin = createAsyncThunk(
  "auth/disablePin",
  async (pin: string, { rejectWithValue, dispatch }) => {
    try {
      const data = await disablePinApi(pin);
      dispatch(updatePinStatus(false));
      await dispatch(fetchAuthMe());

      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for enabling 2FA ----------------------------------------------------
export const enable2FA = createAsyncThunk(
  "auth/enable2FA",
  async (_, { rejectWithValue }) => {
    try {
      const data = await enable2FAApi();

      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for verifying 2FA token
export const verify2FAToken = createAsyncThunk(
  "auth/verify2FAToken",
  async (otpCode: string, { rejectWithValue, dispatch }) => {
    try {
      const data = await verify2FATokenApi(otpCode);
      dispatch(updateTwoFactorStatus(true));
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for disabling 2FA
export const disable2FA = createAsyncThunk(
  "auth/disable2FA",
  async (_, { rejectWithValue }) => {
    try {
      const data = await disable2FAApi();

      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for verifying disable 2FA token
export const verifyDisable2FAToken = createAsyncThunk(
  "auth/verifyDisable2FAToken",
  async (otpCode: string, { rejectWithValue, dispatch }) => {
    try {
      const data = await verifyDisable2FATokenApi(otpCode);
      dispatch(updateTwoFactorStatus(false));
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for sending delete confirmation --------------------------------------------------------
export const sendDeleteConfirmation = createAsyncThunk(
  "auth/sendDeleteConfirmation",
  async (_, { rejectWithValue }) => {
    try {
      const data = await sendDeleteConfirmationApi();
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for confirming account deletion
export const confirmDeleteAccount = createAsyncThunk(
  "auth/confirmDeleteAccount",
  async (code: string, { rejectWithValue }) => {
    try {
      const data = await confirmDeleteAccountApi(code);
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for sending account deletion cancellation confirmation
export const sendReactivateConfirmation = createAsyncThunk(
  "auth/sendReactivateConfirmation",
  async (_, { rejectWithValue }) => {
    try {
      const data = await sendReactivateConfirmationApi();
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for confirming account deletion cancellation
export const confirmReactivateAccount = createAsyncThunk(
  "auth/confirmReactivateAccount",
  async (code: string, { rejectWithValue }) => {
    try {
      const data = await confirmReactivateAccountApi(code);
      return data;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      return rejectWithValue(errorMessage);
    }
  },
);

// Auth slice --------------------------------------------------------------------
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLogout: (state) => {
      state.token = null;
      state.user = null;
      state.error = null;
      state.isLoggedIn = false;
      state.hasCompletedSignup = false;

      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("is_member");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user");
      localStorage.removeItem("language_onboarding_pending");
    },
    setLoggedOutDueTo2FA: (state) => {
      state.isLoggedIn = false;
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("is_member");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user");
    },
    setLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    setHasCompletedSignup: (state) => {
      state.hasCompletedSignup = true;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setUser(state, action: PayloadAction<any>) {
      state.user = { ...action.payload };
      if (action.payload.token) {
        state.token = action.payload.token;
      }
    },
    updatePinStatus(state, action) {
      if (state.user) {
        state.user.pin_security_enabled = action.payload;
      }
    },
    updateTwoFactorStatus(state, action) {
      if (state.user) {
        state.user.two_factor_enabled = action.payload;

        try {
          localStorage.setItem("user", JSON.stringify(state.user));
        } catch (err: any) {
          // Non-invasive logging; avoid console noise in prod
          logger.error("Failed to update user in localStorage", {
            error: err?.message,
          });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder

      // Login user cases
      .addCase(fetchAuthMe.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuthMe.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
        state.isLoggedIn = true;

        /*
         * Only an explicit false means onboarding is incomplete.
         * Missing values are accepted for legacy users.
         */
        const languageCompleted =
          action.payload?.language_onboarding_completed !== false;

        state.hasCompletedSignup = languageCompleted;

        localStorage.setItem("user", JSON.stringify(action.payload));

        if (languageCompleted) {
          localStorage.removeItem("language_onboarding_pending");
        } else {
          localStorage.setItem("language_onboarding_pending", "true");
        }
      })
      .addCase(fetchAuthMe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Register user cases
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.hasCompletedSignup = false;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
        state.hasCompletedSignup = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.hasCompletedSignup = false;
      })

      // Verify user cases
      .addCase(verify.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verify.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.hasCompletedSignup = false;
        state.isLoggedIn = false;
      })
      .addCase(verify.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Answer questions cases
      .addCase(choosePath.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(choosePath.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.successMessage = null;

        const onboardingUser = {
          ...(action.payload.user || {}),
          language_onboarding_completed: false,
        };

        state.user = onboardingUser;
        state.token = action.payload.access;
        state.isLoggedIn = true;
        state.hasCompletedSignup = false;

        localStorage.setItem("access_token", action.payload.access);
        localStorage.setItem("refresh_token", action.payload.refresh);
        localStorage.setItem(
          "is_member",
          action.payload.is_member ? "true" : "false",
        );
        localStorage.setItem("user", JSON.stringify(onboardingUser));
        localStorage.setItem("language_onboarding_pending", "true");

        document.cookie = `access_token=${action.payload.access}; Path=/; Max-Age=300; Secure; SameSite=Lax`;
      })
      .addCase(choosePath.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.error || "Something went wrong.";
      })

      // Profile language onboarding -----------------------------------------------
      .addCase(fetchProfileLanguages.pending, (state) => {
        state.languageLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileLanguages.fulfilled, (state, action) => {
        state.languageLoading = false;
        state.languageOptions = action.payload;
      })
      .addCase(fetchProfileLanguages.rejected, (state, action) => {
        state.languageLoading = false;
        state.error = action.payload as string;
      })

      .addCase(completeProfileLanguage.pending, (state) => {
        state.languageLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(completeProfileLanguage.fulfilled, (state, action) => {
        const completedUser = {
          ...(state.user || {}),
          ...(action.payload.user || {}),
          language_onboarding_completed: true,
        };

        state.languageLoading = false;
        state.error = null;
        state.user = completedUser;
        state.isLoggedIn = true;
        state.hasCompletedSignup = true;
        state.successMessage =
          action.payload.message || "Language saved successfully.";

        localStorage.setItem("user", JSON.stringify(completedUser));
        localStorage.removeItem("language_onboarding_pending");

        markAuthReady();
      })
      .addCase(completeProfileLanguage.rejected, (state, action) => {
        state.languageLoading = false;
        state.successMessage = null;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Unable to save your language.";
      })

      // Login user cases ---------------------------------------------------
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isLoggedIn = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        if (action.payload.is_deleted) {
          markAuthReady();

          state.user = action.payload.user;
          state.token = action.payload.access;
          state.isLoggedIn = true;
          state.hasCompletedSignup = false;

          localStorage.setItem("access_token", action.payload.access);
          localStorage.setItem("refresh_token", action.payload.refresh);
          localStorage.setItem("user_id", String(action.payload.user_id));
          localStorage.setItem(
            "user",
            JSON.stringify(action.payload.user || {}),
          );
          localStorage.removeItem("language_onboarding_pending");

          return;
        }

        const user = action.payload.user;

        /*
         * New users who skipped language onboarding return explicit false.
         * Legacy users may not contain this field and must still log in normally.
         */
        const languageCompleted = user?.language_onboarding_completed !== false;

        state.user = user;
        state.token = action.payload.access;
        state.isLoggedIn = true;
        state.hasCompletedSignup = languageCompleted;

        localStorage.setItem("access_token", action.payload.access);
        localStorage.setItem("refresh_token", action.payload.refresh);
        localStorage.setItem(
          "is_member",
          action.payload.is_member ? "true" : "false",
        );
        localStorage.setItem("user", JSON.stringify(user || {}));

        if (languageCompleted) {
          markAuthReady();

          localStorage.removeItem("language_onboarding_pending");
        } else {
          localStorage.setItem("language_onboarding_pending", "true");
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Login with 2FA cases
      .addCase(loginWith2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
        state.isLoggedIn = false;
      })
      .addCase(loginWith2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.isLoggedIn = true;

        if (action.payload.is_deleted) {
          markAuthReady();

          state.hasCompletedSignup = false;
          state.successMessage =
            action.payload.message ||
            "Your account is scheduled for permanent deletion.";

          localStorage.setItem("access_token", action.payload.access);
          localStorage.setItem("refresh_token", action.payload.refresh);
          localStorage.setItem("user_id", String(action.payload.user_id));
          localStorage.setItem(
            "user",
            JSON.stringify(action.payload.user || {}),
          );

          localStorage.removeItem("is_member");
          localStorage.removeItem("user_email");
          localStorage.removeItem("language_onboarding_pending");

          return;
        }

        const languageCompleted =
          action.payload.user?.language_onboarding_completed !== false;

        state.hasCompletedSignup = languageCompleted;
        state.successMessage =
          action.payload.message || "Logged in successfully with 2FA.";

        localStorage.setItem("access_token", action.payload.access);
        localStorage.setItem("refresh_token", action.payload.refresh);
        localStorage.setItem(
          "is_member",
          action.payload.is_member ? "true" : "false",
        );
        localStorage.setItem("user", JSON.stringify(action.payload.user || {}));
        localStorage.removeItem("user_email");

        if (languageCompleted) {
          markAuthReady();

          localStorage.removeItem("language_onboarding_pending");
        } else {
          localStorage.setItem("language_onboarding_pending", "true");
        }
      })
      .addCase(loginWith2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logout user cases ---------------------------------------------------
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isLoggedIn = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Forget Password cases withouth auth -------------------------
      .addCase(forgetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(forgetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.isLoggedIn = false;
        if (typeof action.payload === "string") {
          state.successMessage = action.payload;
        } else if (
          action.payload &&
          typeof action.payload === "object" &&
          "message" in action.payload
        ) {
          state.successMessage = (
            action.payload as { message: string }
          ).message;
        } else {
          state.successMessage = "Password reset email sent.";
        }
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.loading = false;

        if (typeof action.payload === "string") {
          state.error = action.payload;
        } else if (
          action.payload &&
          typeof action.payload === "object" &&
          "message" in action.payload
        ) {
          state.error = (action.payload as { message: string }).message;
        } else {
          state.error = "Failed to send reset link.";
        }
      })

      // Reset Password cases  withouth auth --------------------------
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetPasswordSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        markAuthReady();
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.access;
        state.isLoggedIn = true;
        state.resetPasswordSuccess = true;

        localStorage.setItem("access_token", action.payload.access);
        localStorage.setItem("refresh_token", action.payload.refresh);
        localStorage.setItem(
          "is_member",
          action.payload.is_member ? "true" : "false",
        );
        localStorage.setItem("user", JSON.stringify(action.payload.user));
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.resetPasswordSuccess = false;
      })

      // Change Password by auth --------------------------------------
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Password changed successfully.";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Enable PIN cases -------------------------------------------------
      .addCase(enablePin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(enablePin.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(enablePin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Disable PIN cases
      .addCase(disablePin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(disablePin.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(disablePin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Enable 2FA --------------------------------------------------------
      .addCase(enable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Verify 2FA Token
      .addCase(verify2FAToken.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(verify2FAToken.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(verify2FAToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Disable 2FA
      .addCase(disable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(disable2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Verify Disable 2FA Token
      .addCase(verifyDisable2FAToken.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(verifyDisable2FAToken.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(verifyDisable2FAToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle send delete confirmation ----------------------------------------
      .addCase(sendDeleteConfirmation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendDeleteConfirmation.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(sendDeleteConfirmation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle confirm delete account
      .addCase(confirmDeleteAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmDeleteAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(confirmDeleteAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle send reactivation confirmation
      .addCase(sendReactivateConfirmation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendReactivateConfirmation.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(sendReactivateConfirmation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Handle confirm reactivation
      .addCase(confirmReactivateAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmReactivateAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(confirmReactivateAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setLogout,
  setLoggedOutDueTo2FA,
  setHasCompletedSignup,
  setLoggedIn,
  clearMessages,
  setUser,
  updatePinStatus,
  updateTwoFactorStatus,
} = authSlice.actions;

export default authSlice.reducer;
